import { ExclamationTriangleIcon, PauseIcon } from '@heroicons/react/20/solid';
import { OfflineBadge } from '../../../components/offline-badge';
import type { ReceivingSessionFile, ReceivingSessionState } from '../../../data/receiving';

type ProgressBarProps = {
  state: string;
  failedFrom?: string | null;
  /** When state is `paused_lpos_busy`, the step the pause happened on — so the
   * bar highlights that step (paused) instead of collapsing to the first one. */
  pausedFrom?: string | null;
  companionName?: string | null;
  companionConnected?: boolean;
  vendorName?: string | null;
  files?: ReceivingSessionFile[];
};

function summarizeFiles(files: ReceivingSessionFile[]): string {
  const noun = files.length === 1 ? 'invoice' : 'invoices';
  const exts = new Set(
    files.map((f) => {
      const match = f.filename.match(/\.([^.]+)$/);

      return match ? match[1].toUpperCase() : '';
    })
  );
  if (exts.size === 1) {
    const [ext] = [...exts];
    if (ext) {
      return `${files.length} ${ext} ${noun}`;
    }
  }

  return `${files.length} ${noun}`;
}

type StepKey = Exclude<ReceivingSessionState, 'processing'> | 'companion';

type Step = {
  key: StepKey;
  label: string;
  doneLabel?: string;
};

// `processing` is no longer its own step — while the backend state is
// `processing` we render the Review pill as `current` (see resolveStepState).
const STEPS: readonly Step[] = [
  { key: 'companion', label: 'Companion' },
  { key: 'selecting_vendor', label: 'Vendor' },
  { key: 'get_invoices', label: 'Invoices' },
  { key: 'review', label: 'Review' },
  { key: 'sending_to_lpos', label: 'Sending', doneLabel: 'Sent' },
  { key: 'waiting_for_lpos', label: 'Receiving' },
  { key: 'complete', label: 'Complete' },
];

type StepStatus = 'done' | 'current' | 'failed' | 'paused' | 'pending';

// Map the session state onto the step the bar should highlight. `processing`
// collapses into `review` so the Review pill goes current while the inline
// spinner (in ActionPane) is on. Also used to resolve failed_from so a
// `failed_from: 'processing'` broadcast from the backend surfaces as a
// failure on the Review pill.
function resolveStepState(state: string): string {
  if (state === 'processing') {
    return 'review';
  }

  if (state === 'selecting_companion') {
    return 'companion';
  }

  return state;
}

function stepStatuses(
  state: string,
  failedFrom?: string | null,
  companionName?: string | null,
  pausedFrom?: string | null
): StepStatus[] {
  const activeIdx = STEPS.findIndex((s) => s.key === resolveStepState(state));
  const failIdx =
    state === 'failed' && failedFrom
      ? STEPS.findIndex((s) => s.key === resolveStepState(failedFrom))
      : -1;
  // paused_lpos_busy isn't a step of its own — resolve the step the pause
  // happened on (paused_from) so the bar shows where we stalled, not step 1.
  const pausedIdx =
    state === 'paused_lpos_busy' && pausedFrom
      ? STEPS.findIndex((s) => s.key === resolveStepState(pausedFrom))
      : -1;

  return STEPS.map((step, i) => {
    if (step.key === 'companion') {
      if (state === 'selecting_companion') {
        return 'current';
      }

      return companionName ? 'done' : 'pending';
    }

    if (state === 'failed') {
      if (failIdx < 0 || i > failIdx) {
        return 'pending';
      }

      return i < failIdx ? 'done' : 'failed';
    }

    if (state === 'paused_lpos_busy' && pausedIdx >= 0) {
      if (i > pausedIdx) {
        return 'pending';
      }

      return i < pausedIdx ? 'done' : 'paused';
    }

    if (activeIdx < 0 || i > activeIdx) {
      return 'pending';
    }

    if (i < activeIdx) {
      return 'done';
    }

    return state === 'complete' ? 'done' : 'current';
  });
}

const HEADER_STYLES: Record<StepStatus, string> = {
  done: 'bg-primary-500 text-white',
  current: 'bg-primary-500 text-white',
  failed: 'bg-red-500 text-white',
  paused: 'bg-amber-500 text-white',
  pending: 'bg-gray-50 text-gray-500',
};

const NUMBER_STYLES: Record<StepStatus, string> = {
  done: 'bg-white text-primary-600',
  current: 'bg-white text-primary-700',
  failed: 'bg-white text-red-600',
  paused: 'bg-white text-amber-600',
  pending: 'bg-gray-200 text-gray-400',
};

function renderStepValue(
  step: Step,
  { companionName, companionConnected, vendorName, files }: ProgressBarProps
) {
  switch (step.key) {
    case 'companion':
      if (!companionName) {
        return null;
      }

      return (
        <>
          <span className="truncate">{companionName}</span>
          {companionConnected === false && <OfflineBadge className="shrink-0" />}
        </>
      );
    case 'selecting_vendor':
      return vendorName ? <span className="truncate">{vendorName}</span> : null;
    case 'get_invoices':
      return files && files.length > 0 ? (
        <span className="truncate">{summarizeFiles(files)}</span>
      ) : null;
    default:
      return null;
  }
}

export function ProgressBar({
  state,
  failedFrom,
  pausedFrom,
  companionName,
  companionConnected,
  vendorName,
  files,
}: ProgressBarProps) {
  const statuses = stepStatuses(state, failedFrom, companionName, pausedFrom);

  return (
    <ol aria-label="Receiving progress" className="flex gap-2 w-full px-4">
      {STEPS.map((step, i) => {
        const status = statuses[i];

        return (
          <li
            key={step.key}
            data-status={status}
            aria-current={status === 'current' || status === 'paused' ? 'step' : undefined}
            className="flex-1 flex flex-col rounded-md overflow-hidden shadow-xs min-w-0"
          >
            <div
              className={`flex items-center gap-2 px-3 py-2 text-sm leading-6 ${HEADER_STYLES[status]}`}
            >
              <span
                className={`inline-flex items-center justify-center h-5 w-5 rounded-full text-xs font-semibold shrink-0 ${NUMBER_STYLES[status]}`}
              >
                {status === 'failed' ? (
                  <ExclamationTriangleIcon className="h-3.5 w-3.5" aria-hidden="true" />
                ) : status === 'paused' ? (
                  <PauseIcon className="h-3.5 w-3.5" aria-hidden="true" />
                ) : (
                  i + 1
                )}
              </span>
              <span className="truncate">
                {status === 'done' && step.doneLabel ? step.doneLabel : step.label}
              </span>
            </div>
            <div className="flex items-center gap-2 px-3 py-1.5 min-h-8 bg-white text-sm text-gray-700">
              {renderStepValue(step, {
                state,
                companionName,
                companionConnected,
                vendorName,
                files,
              })}
            </div>
          </li>
        );
      })}
    </ol>
  );
}
